import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import {
  isCatalogRow,
  isClassification,
  isDataset,
  isDatasetVersion,
} from '@/shared/api/generated/catalog';
import type {
  CatalogRow,
  Classification,
  Dataset,
  DatasetVersion,
} from '@/shared/api/generated/catalog';
import {
  forgetIdempotencyKey,
  getOne,
  getPage,
  idempotencyKeyFor,
  post,
} from '@/shared/api/http';
import type { Page } from '@/shared/api/http';

export interface DatasetQuery {
  readonly search: string;
  readonly connectionId: string | null;
}

export function datasetsUrl({ search, connectionId }: DatasetQuery): string {
  const params = new URLSearchParams();
  if (search.trim().length > 0) params.set('search', search.trim());
  if (connectionId !== null) params.set('connection_id', connectionId);
  params.set('limit', '200');
  return `/v1/catalog/datasets?${params.toString()}`;
}

/* `CatalogRow`, not `Dataset`. The list carries a joined classification and the detail does not, so
 * they are different shapes — and the guard is what enforces that at the boundary rather than trusting
 * the server to have sent the extra fields. */
/* The query is part of the key, so a stale result from a previous search cannot be shown under a
 * new one — the failure mode where a user types, waits, and reads someone else's answer.
 *
 * Extracted so `useDatasetNames` shares this exact cache entry. Two hooks composing the key by hand
 * would drift the moment a filter is added, and the symptom would be a second identical fetch
 * nobody notices. */
function datasetsQueryKey(query: DatasetQuery): readonly unknown[] {
  return ['catalog', 'datasets', query.search.trim(), query.connectionId];
}

export function useDatasets(query: DatasetQuery): UseQueryResult<Page<CatalogRow>> {
  return useQuery({
    queryKey: datasetsQueryKey(query),
    queryFn: () => getPage(datasetsUrl(query), isCatalogRow, 'catalog row'),
    retry: false,
  });
}

/** How many pages of names to walk before stopping. S-76.
 *
 * **A bound, and a reported one.** The point of walking is that a name should not be lost past the
 * 200th dataset; the point of stopping is that an estate of a hundred thousand tables must not make
 * three screens issue five hundred requests to render one column. Twenty pages of 200 is 4,000
 * datasets, which covers a large BFSI estate — and `truncatedNames` is what tells a caller the walk
 * stopped, so a missing name reads as "beyond what we fetched" rather than as "no such dataset".
 *
 * That is the distinction this hook existed to lose. Before this it fetched exactly one capped page
 * and a caller could not tell an unlisted dataset from an absent one, which is why the same bare-UUID
 * defect has been recorded four times.
 */
const MAX_NAME_PAGES = 20;

export interface DatasetNames {
  /** Ids to qualified names, for every dataset the walk reached. */
  readonly byId: ReadonlyMap<string, string>;
  /** True when the walk stopped at `MAX_NAME_PAGES` with a cursor still outstanding.
   *
   * Reported rather than swallowed: a caller rendering "not in the current catalogue listing" is
   * making a claim about the catalogue, and it is only honest if the listing was exhausted.
   */
  readonly truncated: boolean;
}

/* Dataset ids to qualified names, for screens that hold an id and owe the reader a name.
 *
 * Exported through the feature's index because three other screens need it and the alternatives are
 * worse in both directions: a per-row `useDataset` is an N+1 the catalog list endpoint exists to
 * avoid, and rendering the bare UUID is the defect this codebase has now shipped four times — the
 * lineage impact answer, the edge inspector, the PII review queue and the anomaly findings table.
 *
 * **It walks `/datasets/all` rather than reading the capped `/datasets`.** That was the actual cause
 * of the fourth instance: this hook fetched one 200-row page, so a finding about the 201st dataset
 * had no name to find and the cell fell back to an id. D-05 made the listing enumerable; this is the
 * consumer that makes it visible.
 *
 * Its own cache key, no longer shared with `useDatasets`. The two now read *different routes* — the
 * screen wants one bounded page and this wants every name — so sharing a key would mean whichever
 * mounted first decided what the other got. That sharing was correct while both read one URL and is
 * exactly the kind of thing that keeps working while quietly meaning something else.
 */
export function useDatasetNames(): UseQueryResult<DatasetNames> {
  return useQuery({
    queryKey: ['catalog', 'dataset-names'],
    queryFn: fetchAllDatasetNames,
    retry: false,
  });
}

async function fetchAllDatasetNames(): Promise<DatasetNames> {
  const byId = new Map<string, string>();
  let cursor: string | null = null;

  for (let page = 0; page < MAX_NAME_PAGES; page += 1) {
    const params = new URLSearchParams({ limit: '200' });
    if (cursor !== null) params.set('after', cursor);
    const result: Page<CatalogRow> = await getPage(
      `/v1/catalog/datasets/all?${params.toString()}`,
      isCatalogRow,
      'catalog row',
    );
    for (const row of result.items) {
      byId.set(row.id, `${row.schema_name}.${row.object_name}`);
    }
    /* `null` means the last page. An empty string would be a cursor the server never mints, and
     * treating it as "more" is how a loop runs to its bound on a one-page estate — `getPage`
     * normalises it, and this reads the normalised value. */
    cursor = result.nextCursor;
    if (cursor === null) return { byId, truncated: false };
  }
  return { byId, truncated: true };
}

/** The enumerable browse, page by page. S-78.
 *
 * **A second hook rather than a parameter on `useDatasets`**, and the split is deliberate: the two
 * read *different routes* for different reasons. `useDatasets` reads the bounded `/datasets`, which is
 * what `DatasetDetail`'s neighbours and the score join want — one page, no cursor, no state to carry.
 * This reads `/datasets/all`, which is what a person browsing an estate wants.
 *
 * Collapsing them into one hook with an optional cursor would make the URL depend on whether a caller
 * happened to pass one, so a screen that forgot would silently get the capped route back — which is
 * exactly the regression S-76 had to fix in `useDatasetNames`.
 *
 * The cursor is part of the query key. Without it React Query would serve page 1 from cache under
 * page 2's request, which is the failure mode where a user clicks Next and reads the same rows.
 */
export function usePagedDatasets(
  query: DatasetQuery,
  cursor: string | null,
): UseQueryResult<Page<CatalogRow>> {
  return useQuery({
    queryKey: [...datasetsQueryKey(query), 'paged', cursor],
    queryFn: () => getPage(pagedDatasetsUrl(query, cursor), isCatalogRow, 'catalog row'),
    retry: false,
  });
}

export function pagedDatasetsUrl(
  { search, connectionId }: DatasetQuery,
  cursor: string | null,
): string {
  const params = new URLSearchParams();
  if (search.trim().length > 0) params.set('search', search.trim());
  if (connectionId !== null) params.set('connection_id', connectionId);
  params.set('limit', String(DATASETS_PER_PAGE));
  /* Appended last and only when present. An `after=` with an empty value is a cursor the server
   * never minted, and it would be refused with a 400 — the same message a forged one gets, which is
   * a confusing way to say "this is page 1". */
  if (cursor !== null) params.set('after', cursor);
  return `/v1/catalog/datasets/all?${params.toString()}`;
}

/** How many datasets a page of the browser holds.
 *
 * 50 rather than the route's 200 maximum, because this is a table a person reads: 200 rows is a
 * scroll, and the point of paging is that the page is the unit somebody looks at. The name-lookup
 * walk uses 200 for the opposite reason — nobody reads it, so fewer round trips is the only axis.
 */
export const DATASETS_PER_PAGE = 50;

export function useDataset(datasetId: string): UseQueryResult<Dataset> {
  return useQuery({
    queryKey: ['catalog', 'dataset', datasetId],
    queryFn: () =>
      getOne(
        `/v1/catalog/datasets/${encodeURIComponent(datasetId)}`,
        isDataset,
        'dataset',
      ),
    retry: false,
  });
}

export function useSchemaHistory(
  datasetId: string,
): UseQueryResult<Page<DatasetVersion>> {
  return useQuery({
    queryKey: ['catalog', 'dataset', datasetId, 'versions'],
    queryFn: () =>
      getPage(
        `/v1/catalog/datasets/${encodeURIComponent(datasetId)}/versions`,
        isDatasetVersion,
        'dataset version',
      ),
    retry: false,
  });
}

/* A dataset that has never been classified 404s, and that is a legitimate answer rather than a
 * failure — most datasets in a fresh catalog have no verdict yet. Distinguished from a real error so
 * the screen can say "not classified" instead of "could not load". */
export const NOT_CLASSIFIED = Symbol('not-classified');

export function useClassification(
  datasetId: string,
): UseQueryResult<Classification | typeof NOT_CLASSIFIED> {
  return useQuery({
    queryKey: ['catalog', 'dataset', datasetId, 'classification'],
    queryFn: async (): Promise<Classification | typeof NOT_CLASSIFIED> => {
      try {
        return await getOne(
          `/v1/catalog/datasets/${encodeURIComponent(datasetId)}/classification`,
          isClassification,
          'classification',
        );
      } catch (error) {
        if (error instanceof Error && 'status' in error && error.status === 404) {
          return NOT_CLASSIFIED;
        }
        throw error;
      }
    },
    retry: false,
  });
}

/** One `POST /classify`, as a plain function.
 *
 * Extracted for `requestProfile`'s reason and no other: a hook is bound to one `datasetId`, and the
 * bulk control has to run this for many. The alternative was a second copy of the URL, the
 * idempotency scope and the key lifecycle, which is the duplication that drifts silently — both
 * copies read correctly in isolation.
 *
 * Byte-for-byte the request the hook below used to make, including `encodeURIComponent` on the id
 * and the `finally` that clears the key.
 */
export async function requestClassification(datasetId: string): Promise<Classification> {
  const key = idempotencyKeyFor('catalog.classify', datasetId);
  try {
    return await post(
      `/v1/catalog/datasets/${encodeURIComponent(datasetId)}/classify`,
      key,
      isClassification,
      'classification',
    );
  } finally {
    forgetIdempotencyKey('catalog.classify', datasetId);
  }
}

export function useRequestClassification(datasetId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => requestClassification(datasetId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['catalog', 'dataset', datasetId, 'classification'],
      });
      void queryClient.invalidateQueries({ queryKey: ['catalog', 'datasets'] });
    },
  });
}
